/**
 * SkuMultiSelect — controlled multi-select over the SKU catalog, grouped by
 * product family.
 *
 * Partners hold whole garment families ("classic tee in every size and
 * colour"), so the picker selects at the product level by default: one
 * tri-state checkbox per family adds/removes every variant, with an expandable
 * row for per-SKU control. Search + a POD-blanks/PAP filter narrow the list,
 * and "Select all shown" bulk-adds the filtered set. Backed by
 * `inventory.listSkus`; controlled (parent owns the selected-id list) so it
 * composes inside a form.
 */
import { useMemo, useState } from 'react';
import { ChevronRight, Loader2, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

type CatalogSku = {
  id: string;
  sku: string;
  title: string | null;
  category: { type: string | null; size: string | null; color: string | null };
  productionType: string | null;
  isLocked: boolean;
};

type SkuGroup = {
  key: string;
  name: string;
  skus: CatalogSku[];
};

type TypeFilter = 'all' | 'blanks' | 'pap';

const TYPE_FILTERS: Array<{ value: TypeFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'blanks', label: 'POD blanks' },
  { value: 'pap', label: 'Pick & pack' },
];

const matchesTypeFilter = (s: CatalogSku, filter: TypeFilter): boolean => {
  if (filter === 'blanks') return s.productionType === 'POD' && !s.isLocked;
  if (filter === 'pap') return s.productionType === 'PAP';
  return true;
};

const variantLabel = (s: CatalogSku): string =>
  [s.category.color, s.category.size].filter(Boolean).join(' / ');

export function SkuMultiSelect({ value, onChange, disabled }: Props) {
  const skusQuery = trpc.inventory.listSkus.useQuery({}, { staleTime: 60_000 });
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const allSkus = useMemo(() => {
    const raw = (skusQuery.data ?? []) as CatalogSku[];
    // listSkus can return one row per (sku, location); de-dupe by sku id.
    const byId = new Map<string, CatalogSku>();
    for (const s of raw) if (!byId.has(s.id)) byId.set(s.id, s);
    return Array.from(byId.values());
  }, [skusQuery.data]);

  const skuById = useMemo(() => new Map(allSkus.map((s) => [s.id, s])), [allSkus]);
  const selected = useMemo(() => new Set(value), [value]);

  const groups = useMemo<SkuGroup[]>(() => {
    const byKey = new Map<string, SkuGroup>();
    for (const s of allSkus) {
      const name = s.title?.trim() || s.category.type || 'Other';
      const key = name.toLowerCase();
      let group = byKey.get(key);
      if (!group) {
        group = { key, name, skus: [] };
        byKey.set(key, group);
      }
      group.skus.push(s);
    }
    const list = Array.from(byKey.values());
    for (const g of list) g.skus.sort((a, b) => a.sku.localeCompare(b.sku));
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [allSkus]);

  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return groups
      .map((g) => {
        const skus = g.skus.filter((s) => matchesTypeFilter(s, typeFilter));
        if (skus.length === 0) return null;
        if (!term) return { ...g, skus };
        if (g.name.toLowerCase().includes(term)) return { ...g, skus };
        const matching = skus.filter(
          (s) =>
            s.sku.toLowerCase().includes(term) ||
            variantLabel(s).toLowerCase().includes(term),
        );
        return matching.length > 0 ? { ...g, skus: matching } : null;
      })
      .filter((g): g is SkuGroup => g !== null);
  }, [groups, search, typeFilter]);

  const visibleSkuCount = useMemo(
    () => visibleGroups.reduce((n, g) => n + g.skus.length, 0),
    [visibleGroups],
  );
  const visibleSelectedCount = useMemo(
    () => visibleGroups.reduce((n, g) => n + g.skus.filter((s) => selected.has(s.id)).length, 0),
    [visibleGroups, selected],
  );

  // Selected SKUs summarised per family — selecting whole garment families
  // produces hundreds of ids, so per-SKU chips don't scale.
  const selectedSummary = useMemo(() => {
    const byKey = new Map<string, { name: string; ids: string[] }>();
    for (const id of value) {
      const s = skuById.get(id);
      const name = s ? s.title?.trim() || s.category.type || 'Other' : 'Unknown SKU';
      const key = name.toLowerCase();
      const entry = byKey.get(key) ?? { name, ids: [] };
      entry.ids.push(id);
      byKey.set(key, entry);
    }
    return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [value, skuById]);

  const addIds = (ids: string[]) => {
    if (disabled || ids.length === 0) return;
    const next = new Set(value);
    for (const id of ids) next.add(id);
    onChange(Array.from(next));
  };
  const removeIds = (ids: string[]) => {
    if (disabled || ids.length === 0) return;
    const drop = new Set(ids);
    onChange(value.filter((v) => !drop.has(v)));
  };

  const toggleGroup = (g: SkuGroup) => {
    const ids = g.skus.map((s) => s.id);
    const allIn = ids.every((id) => selected.has(id));
    if (allIn) removeIds(ids);
    else addIds(ids);
  };

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-2">
      {value.length > 0 ? (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">
            {value.length} SKU{value.length === 1 ? '' : 's'} selected
          </p>
          <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
            {selectedSummary.map((entry) => (
              <span
                key={entry.name}
                className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-1 text-xs font-medium"
              >
                <span className="truncate max-w-[16rem]">{entry.name}</span>
                <span className="text-muted-foreground">· {entry.ids.length}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeIds(entry.ids)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Remove ${entry.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No SKUs chosen yet.</p>
      )}

      {!disabled && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search products or SKU codes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-xs"
            />
            <div className="flex shrink-0 rounded-md border p-0.5">
              {TYPE_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setTypeFilter(f.value)}
                  className={cn(
                    'rounded px-2 py-1 text-[11px] font-medium transition-colors',
                    typeFilter === f.value
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>
              {visibleGroups.length} product{visibleGroups.length === 1 ? '' : 's'} ·{' '}
              {visibleSkuCount} SKU{visibleSkuCount === 1 ? '' : 's'} shown
            </span>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px]"
                disabled={visibleSelectedCount === visibleSkuCount || visibleSkuCount === 0}
                onClick={() => addIds(visibleGroups.flatMap((g) => g.skus.map((s) => s.id)))}
              >
                Select all shown
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px]"
                disabled={visibleSelectedCount === 0}
                onClick={() => removeIds(visibleGroups.flatMap((g) => g.skus.map((s) => s.id)))}
              >
                Clear shown
              </Button>
            </div>
          </div>

          <div className="max-h-72 overflow-auto rounded-md border bg-muted/20">
            {skusQuery.isLoading ? (
              <p className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading catalog…
              </p>
            ) : visibleGroups.length === 0 ? (
              <p className="px-2 py-2 text-xs text-muted-foreground">No matches.</p>
            ) : (
              visibleGroups.map((g) => {
                const selectedInGroup = g.skus.filter((s) => selected.has(s.id)).length;
                const allIn = selectedInGroup === g.skus.length;
                const isExpanded = expanded.has(g.key);
                return (
                  <div key={g.key} className="border-b last:border-b-0">
                    <div className="flex items-center gap-2 px-2 py-1.5 hover:bg-background">
                      <Checkbox
                        checked={allIn ? true : selectedInGroup > 0 ? 'indeterminate' : false}
                        onCheckedChange={() => toggleGroup(g)}
                        aria-label={`Select all ${g.name} SKUs`}
                      />
                      <button
                        type="button"
                        onClick={() => toggleExpanded(g.key)}
                        className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                      >
                        <span className="truncate text-xs font-medium">{g.name}</span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {selectedInGroup > 0 ? `${selectedInGroup}/` : ''}
                          {g.skus.length} SKU{g.skus.length === 1 ? '' : 's'}
                        </span>
                        <ChevronRight
                          className={cn(
                            'ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground/60 transition-transform',
                            isExpanded && 'rotate-90',
                          )}
                        />
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="space-y-0.5 pb-1.5 pl-7 pr-2">
                        {g.skus.map((s) => (
                          <label
                            key={s.id}
                            className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-background"
                          >
                            <Checkbox
                              checked={selected.has(s.id)}
                              onCheckedChange={(checked) =>
                                checked ? addIds([s.id]) : removeIds([s.id])
                              }
                              aria-label={`Select ${s.sku}`}
                            />
                            <span className="font-mono text-[11px]">{s.sku}</span>
                            {variantLabel(s) && (
                              <span className="truncate text-[11px] text-muted-foreground">
                                {variantLabel(s)}
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
